import { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { useNavigate, Outlet, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getFormularios, createFormulario, getFormularioRespuestas } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useUnreadRespuestas } from "@/contexts/UnreadRespuestasContext";
import { getDotPatternStyle } from "@/components/AppBackground";
import { AppMainHeader } from "@/components/AppMainHeader";
import { MobileBackBar } from "@/components/MobileBackBar";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function FormulariosLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const inFormulariosSection = (location.pathname || "").startsWith("/formularios");
  const { id: currentId } = useParams();
  const { user, authLoading } = useAuth();
  const settings = useSettings();
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewUpdates, setPreviewUpdates] = useState({});

  const setFormularioPreview = useCallback((formularioId, data) => {
    setPreviewUpdates((prev) => {
      if (data == null) {
        const next = { ...prev };
        delete next[formularioId];
        return next;
      }
      return {
        ...prev,
        [formularioId]: { ...(prev[formularioId] || {}), ...data },
      };
    });
  }, []);

  /** Registrado por FormulariosLayoutContent para anteponer el id al orden del sidebar (localStorage). */
  const formSidebarOrderRef = useRef({});

  const addFormularioToSidebarList = useCallback((item) => {
    if (!item?.id) return;
    setFormularios((prev) => {
      if (prev.some((f) => f.id === item.id)) return prev;
      const row = {
        id: item.id,
        titulo: item.titulo ?? "",
        slug: item.slug ?? "",
        fecha_creacion: item.fecha_creacion ?? new Date().toISOString(),
        ...(item.emoji != null && item.emoji !== "" ? { emoji: item.emoji } : {}),
      };
      return [row, ...prev];
    });
    queueMicrotask(() => formSidebarOrderRef.current.prependId?.(item.id));
  }, []);

  const removeFormularioFromSidebarList = useCallback((formularioId) => {
    if (!formularioId) return;
    setFormularios((prev) => prev.filter((f) => f.id !== formularioId));
    setPreviewUpdates((prev) => {
      if (!prev[formularioId]) return prev;
      const next = { ...prev };
      delete next[formularioId];
      return next;
    });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.formularios === false) {
      if (user.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else if (user.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
      else if (user.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user.permissions?.proveedores !== false) navigate("/proveedores", { replace: true });
      else if (user.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
      else if (user.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
      return;
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.permissions?.formularios === false) return;
    if (!user) return;
    if (!inFormulariosSection) return;
    setLoading(true);
    getFormularios()
      .then((data) => setFormularios(data.formularios || []))
      .catch(() => {
        toast.error("Error al cargar formularios");
        setFormularios([]);
      })
      .finally(() => setLoading(false));
  }, [user, inFormulariosSection]);

  const handleAddFormulario = async () => {
    setCreating(true);
    try {
      const data = await createFormulario("Nuevo formulario");
      const id = data?.id;
      if (id) {
        addFormularioToSidebarList({
          id: data.id,
          titulo: data.titulo,
          slug: data.slug,
          fecha_creacion: new Date().toISOString(),
        });
        toast.success("Formulario creado");
        navigate(`/formularios/${id}`);
      } else {
        toast.error("No se devolvió el id del formulario");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear formulario");
    } finally {
      setCreating(false);
    }
  };

  const dotStyle = getDotPatternStyle(settings);

  if (authLoading || !user || user.permissions?.formularios === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <FormulariosLayoutContent
      navigate={navigate}
      currentId={currentId}
      formularios={formularios}
      loading={loading}
      creating={creating}
      handleAddFormulario={handleAddFormulario}
      dotStyle={dotStyle}
      previewUpdates={previewUpdates}
      setFormularioPreview={setFormularioPreview}
      formSidebarOrderRef={formSidebarOrderRef}
      addFormularioToSidebarList={addFormularioToSidebarList}
      removeFormularioFromSidebarList={removeFormularioFromSidebarList}
    />
  );
}

function formatFormularioFecha(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}h | ${day}.${month}.${year}`;
}

const EMOJI_PREDETERMINADO = "📄";

function nonEmptyString(v) {
  return typeof v === "string" && v.trim() !== "";
}

/** Evita que preview { titulo: "" } pise el título de la lista (efecto del detalle antes de cargar). */
function sidebarTituloFromPreview(preview, listTitulo) {
  if (nonEmptyString(preview?.titulo)) return preview.titulo;
  return listTitulo || "Sin título";
}

function sidebarEmojiFromPreview(preview, listEmoji, fallback) {
  if (nonEmptyString(preview?.emoji)) return preview.emoji;
  return listEmoji ?? fallback;
}

function FormulariosLayoutContent({
  navigate,
  currentId,
  formularios,
  loading,
  creating,
  handleAddFormulario,
  dotStyle,
  previewUpdates = {},
  setFormularioPreview = () => {},
  formSidebarOrderRef,
  addFormularioToSidebarList,
  removeFormularioFromSidebarList,
}) {
  const { unreadByFormulario, setBulkUnreadByFormulario, readRespuestaIdsByFormulario } = useUnreadRespuestas();
  const [formOrderIds, setFormOrderIds] = useState(() => {
    try {
      const raw = localStorage.getItem("formularios-order-ids");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [dragFormularioId, setDragFormularioId] = useState("");
  const currentFormulario = currentId && formularios?.length ? formularios.find((f) => f.id === currentId) : null;
  const currentFormPreview = currentId ? previewUpdates[currentId] : null;
  const headerTitulo = nonEmptyString(currentFormPreview?.titulo)
    ? currentFormPreview.titulo
    : (currentFormulario?.titulo || "Editar");
  const titleSuffix = currentId && formularios?.length ? ` / ${headerTitulo}` : "";
  const orderedFormularios = (() => {
    if (!formularios?.length) return [];
    if (!formOrderIds?.length) return formularios;
    const byId = new Map(formularios.map((f) => [f.id, f]));
    const ordered = [];
    for (const id of formOrderIds) {
      if (byId.has(id)) ordered.push(byId.get(id));
    }
    for (const f of formularios) {
      if (!formOrderIds.includes(f.id)) ordered.push(f);
    }
    return ordered;
  })();

  const persistFormOrder = (ids) => {
    setFormOrderIds(ids);
    try {
      localStorage.setItem("formularios-order-ids", JSON.stringify(ids));
    } catch (_) {}
  };

  useLayoutEffect(() => {
    if (!formSidebarOrderRef) return undefined;
    formSidebarOrderRef.current.prependId = (fid) => {
      if (!fid) return;
      setFormOrderIds((prev) => {
        const next = [fid, ...prev.filter((x) => x !== fid)];
        try {
          localStorage.setItem("formularios-order-ids", JSON.stringify(next));
        } catch (_) {}
        return next;
      });
    };
    return () => {
      delete formSidebarOrderRef.current.prependId;
    };
  }, [formSidebarOrderRef]);

  useEffect(() => {
    if (!formularios?.length) return;
    const ids = formularios.map((f) => f.id);
    if (!formOrderIds.length) {
      persistFormOrder(ids);
      return;
    }
    const cleaned = formOrderIds.filter((id) => ids.includes(id));
    const missing = ids.filter((id) => !cleaned.includes(id));
    if (cleaned.length !== formOrderIds.length || missing.length > 0) {
      persistFormOrder([...cleaned, ...missing]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formularios, formOrderIds]);

  // Refresca badges de respuestas sin leer para todos los formularios del sidebar.
  useEffect(() => {
    if (!formularios?.length) {
      setBulkUnreadByFormulario({});
      return;
    }
    let cancelled = false;
    const refreshUnread = async () => {
      try {
        const results = await Promise.all(
          formularios.map((f) =>
            getFormularioRespuestas(f.id)
              .then((d) => ({ id: f.id, respuestas: d?.respuestas || [] }))
              .catch(() => ({ id: f.id, respuestas: [] }))
          )
        );
        if (cancelled) return;
        const bulk = {};
        results.forEach(({ id, respuestas }) => {
          const readByFormulario = readRespuestaIdsByFormulario[id] || {};
          bulk[id] = respuestas.filter((r) => !readByFormulario[r.id]).length;
        });
        setBulkUnreadByFormulario(bulk);
      } catch (_) {}
    };
    refreshUnread();
    const timer = setInterval(refreshUnread, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [formularios, setBulkUnreadByFormulario, readRespuestaIdsByFormulario]);

  const reorderFormularios = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const current = [...(formOrderIds?.length ? formOrderIds : formularios.map((f) => f.id))];
    const fromIdx = current.indexOf(fromId);
    const toIdx = current.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    persistFormOrder(current);
  };

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Formularios" titleSuffix={titleSuffix} titleAs="h5" />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className={`${currentId ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 flex-col md:border-r border-gray-300 bg-background overflow-hidden`}>
          <div className="p-4 shrink-0 border-b border-gray-200">
            <Button
              onClick={handleAddFormulario}
              disabled={creating}
              className="gap-2 w-full"
              data-testid="add-formulario-btn"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              ) : (
                <Plus className="w-4 h-4" strokeWidth={1.5} aria-hidden />
              )}
              Añadir formulario
            </Button>
          </div>
          <div className="p-4 flex-1 overflow-auto mobile-nav-scroll">
            {loading && formularios.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin shrink-0" aria-hidden />
                <span>Cargando formularios…</span>
              </div>
            ) : formularios.length === 0 ? (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle className="text-base">No hay formularios</CardTitle>
                  <CardDescription>
                    Crea el primer formulario con el botón «Añadir formulario» y luego edita título, slug y bloques.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleAddFormulario} disabled={creating} className="gap-2">
                    {creating ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    ) : (
                      <Plus className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                    )}
                    Añadir formulario
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-2 mb-4" role="list">
                {orderedFormularios.map((f) => {
                  const unread = unreadByFormulario[f.id] ?? 0;
                  const preview = previewUpdates[f.id];
                  const emoji = sidebarEmojiFromPreview(preview, f.emoji, EMOJI_PREDETERMINADO);
                  const titulo = sidebarTituloFromPreview(preview, f.titulo);
                  const isActive = currentId === f.id;
                  return (
                    <li key={f.id}>
                      <Card
                        className={`transition-colors shadow-none hover:bg-muted/50 ${isActive ? "bg-muted/40" : ""}`}
                        draggable
                        onDragStart={() => setDragFormularioId(f.id)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          reorderFormularios(dragFormularioId, f.id);
                          setDragFormularioId("");
                        }}
                      >
                        <CardContent className="p-3 py-3">
                          <button
                            type="button"
                            className="flex gap-3 w-full min-w-0 text-left rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            onClick={() => navigate(`/formularios/${f.id}`)}
                          >
                            <div className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center">
                              <span className="text-2xl leading-none select-none inline-block" aria-hidden style={{ textShadow: "rgba(0, 0, 0, 0.3) 0px 1px 2px" }}>
                                {emoji}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                              <p className="text-sm font-medium flex items-center gap-2">
                                <span className="truncate flex-1 min-w-0">{titulo || "Sin título"}</span>
                                {unread > 0 && (
                                  <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">
                                    {unread}
                                  </span>
                                )}
                              </p>
                              {f.fecha_creacion && (
                                <p className="text-xs text-muted-foreground">
                                  {formatFormularioFecha(f.fecha_creacion)}
                                </p>
                              )}
                            </div>
                          </button>
                        </CardContent>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        <div className={`${currentId ? "" : "hidden md:block"} flex-1 min-w-0 overflow-y-auto relative mobile-nav-scroll`} style={dotStyle}>
          {currentId && <MobileBackBar to="/formularios" label="Formularios" />}
          <Outlet
            context={{
              setFormularioPreview,
              addFormularioToSidebarList,
              removeFormularioFromSidebarList,
            }}
          />
        </div>
      </div>
    </div>
  );
}

